/**
 * Normalize payment rows from varying API shapes (flat vs nested relations).
 */

const getLead = (row) =>
  row?.invoice?.order?.lead ?? row?.invoice?.lead ?? null;

export const hasLeadData = (row) => {
  const lead = getLead(row);
  if (!lead) return false;
  return (
    getLeadNameDisplay(row) !== "—" ||
    getLeadEmailDisplay(row) !== "—" ||
    getLeadPhoneDisplay(row) !== "—"
  );
};

export const getLeadNameDisplay = (row) => {
  const lead = getLead(row);
  const v = lead?.name ?? lead?.dba_name;
  return v != null && String(v).trim() !== "" ? String(v).trim() : "—";
};

export const getLeadEmailDisplay = (row) => {
  const v = getLead(row)?.email;
  return v != null && String(v).trim() !== "" ? String(v).trim() : "—";
};

export const getLeadPhoneDisplay = (row) => {
  const v = getLead(row)?.phone;
  return v != null && String(v).trim() !== "" ? String(v).trim() : "—";
};

const getPaymentTypeObject = (row) => {
  if (row?.paymentType && typeof row.paymentType === "object")
    return row.paymentType;
  if (row?.payment_type && typeof row.payment_type === "object")
    return row.payment_type;
  return null;
};

/** Match card types across slug/code/name (same idea as CreateInvoice / InvoiceList). */
export const isPaymentTypeCard = (row) => {
  const pt = getPaymentTypeObject(row);
  if (!pt) {
    const label = String(
      row?.payment_type_name ??
        (typeof row?.payment_type === "string" ? row.payment_type : ""),
    ).toLowerCase();
    return (
      label === "card" || label === "credit card" || /\bcard\b/.test(label)
    );
  }
  const slug = String(pt.slug ?? pt.code ?? "").toLowerCase();
  if (slug === "card" || slug === "credit_card" || slug === "credit-card")
    return true;
  const name = String(pt.name || "")
    .trim()
    .toLowerCase();
  if (name === "card" || name === "credit card") return true;
  if (/\bcard\b/i.test(String(pt.name || ""))) return true;
  return false;
};

export const canChangePaymentStatus = (row) => !isPaymentTypeCard(row);

export const getPaymentTypeLabel = (row) =>
  row?.paymentType?.name ??
  row?.payment_type?.name ??
  row?.payment_type_name ??
  (typeof row?.payment_type === "string" ? row.payment_type : null) ??
  "—";

export const getGatewayIdDisplay = (row) =>
  row?.payment_gateway?.name ?? row?.paymentGateway?.name ?? "—";

const getInvoice = (row) => row?.invoice;

export const getInvoiceStatusDisplay = (row) => {
  const s = getInvoice(row)?.status;
  if (s && typeof s === "object") return s.name ?? s.label ?? s.code ?? "—";
  if (typeof s === "string" && s.trim() !== "") return s.trim();
  return "—";
};

// invoices.invoice_url / type_of_payment were dropped when invoices became
// order-centric: the pay link is now the computed `payment_url`, and the kind
// of payment comes from the payment_type relation.
export const getInvoiceUrlDisplay = (row) => {
  const v = getInvoice(row)?.payment_url;
  return v != null && String(v).trim() !== "" ? String(v).trim() : "";
};

export const getInvoiceTypeOfPaymentDisplay = (row) => {
  const inv = getInvoice(row);
  const v = inv?.payment_type?.name ?? inv?.paymentType?.name;
  return v != null && String(v).trim() !== "" ? String(v).trim() : "—";
};

export const getInvoiceSaleTypeDisplay = (row) => {
  const inv = getInvoice(row);
  const v = inv?.order?.sale_type ?? inv?.sale_type;
  return v != null && String(v).trim() !== ""
    ? String(v).replace(/_/g, " ").replace(/-/g, " ")
    : "—";
};

// Commented out because its not used anymore, but kept for reference in case we need it in the future

// export const getInvoiceShouldSendContractDisplay = (row) => {
//   const v = getInvoice(row)?.should_send_contract;
//   if (v === true || v === 1 || v === '1') return 'Yes';
//   if (v === false || v === 0 || v === '0') return 'No';
//   if (v != null && String(v).trim() !== '') return String(v).trim();
//   return '—';
// };

export const getInvoiceCreatedAtDisplay = (row) => {
  const raw = getInvoice(row)?.created_at;
  if (!raw || String(raw).trim() === "") return "—";
  const d = new Date(String(raw).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(raw) : d.toLocaleString();
};

// Commented out because its not used anymore, but kept for reference in case we need it in the future

// export const getInvoiceDescriptionDisplay = (row) => {
//   const v = getInvoice(row)?.description;
//   return v != null && String(v).trim() !== '' ? String(v).trim() : '—';
// };

export const getTransactionIdDisplay = (row) =>
  row?.transaction_id ??
  row?.reference_id ??
  row?.reference ??
  row?.external_id ??
  "—";

export const getAmountDisplay = (row) => {
  const v = row?.amount ?? row?.invoice?.amount;
  if (v === undefined || v === null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const getTimeDisplay = (row) => {
  const raw = row?.transaction_date_time;
  if (
    raw === undefined ||
    raw === null ||
    String(raw).trim() === "" ||
    raw === "-"
  ) {
    return "—";
  }
  const d = new Date(String(raw).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(raw) : d.toLocaleString();
};

export const getNoteDisplay = (row) => {
  const v = row?.note;
  return v != null && String(v).trim() !== "" ? String(v).trim() : "—";
};

export const getCcTypeDisplay = (row) => {
  const v = row?.cc_type;
  return v != null && String(v).trim() !== "" ? String(v).trim() : "—";
};

// Commented out because its not used anymore, but kept for reference in case we need it in the future

// export const getTypeDisplay = (row) =>
//   row?.type ??
//   row?.payment_kind ??
//   row?.direction ??
//   row?.transaction_type ??
//   '—';

export const getStatusDisplay = (row) => {
  const s = row?.status;
  if (s && typeof s === "object") return s.name ?? s.label ?? s.code ?? "—";
  if (typeof s === "string" && s.trim() !== "") return s.trim();
  return row?.status_name ?? row?.payment_status ?? "—";
};

export const getClientIpDisplay = (row) =>
  row?.client_ip ?? row?.ip_address ?? row?.ip ?? row?.request_ip ?? "—";

export const getBrandDisplay = (row) => {
  const v =
    row?.brand?.name ??
    row?.invoice?.brand?.name ??
    row?.invoice?.order?.brand?.name;
  return v != null && String(v).trim() !== "" ? String(v).trim() : "—";
};

export const getAccountDescriptorDisplay = (row) => {
  const v =
    row?.account?.descriptor ??
    row?.invoice?.account?.descriptor ??
    row?.invoice?.order?.account?.descriptor ??
    row?.account?.name ??
    row?.invoice?.account?.name ??
    row?.invoice?.order?.account?.name;
  return v != null && String(v).trim() !== "" ? String(v).trim() : "—";
};
